<script lang="ts">
  import type { StoresKey } from "~/stores";
  import { getStoresContext } from "~/stores";

  export let request_store_key: StoresKey;
  export let useIcons: boolean = true;
  export let iconSize: string = 'base';
  export let websiteIconSrc: string = '';
  export let emailIconSrc: string = '';
  export let phoneIconSrc: string = '';

  const { store } = getStoresContext(request_store_key);

  /**
   * Sanitize and format store's website URL
   * - Handle empty/undefined values
   * - Ensure protocol is https:// (convert from http: if needed)
   * - Validate URL format with a URL constructor
   * - Handle common URL formatting issues
   */
  function sanitizeUrl(url: string | null | undefined): string {
    // Handle empty values
    if (!url) return "";
    
    // Trim whitespace
    let cleanUrl = url.trim();
    if (!cleanUrl) return "";
    
    try {
      // If URL doesn't start with a protocol, add https://
      if (!cleanUrl.match(/^[a-zA-Z]+:\/\//)) {
        cleanUrl = `https://${cleanUrl}`;
      }
      
      // Convert http:// to https:// for security
      if (cleanUrl.startsWith('http://')) {
        cleanUrl = cleanUrl.replace(/^http:\/\//, 'https://');
      }
      
      // Validate URL format using a URL constructor
      // This will throw an error if the URL is invalid
      const urlObj = new URL(cleanUrl);
      
      // Return the sanitized URL
      return urlObj.href;
    } catch (e) {
      // If URL parsing fails, return empty string
      console.error('Invalid URL:', url, e);
      return "";
    }
  }
  
  /**
   * Format URL for display by removing protocol and www, capitalizing the first letter,
   * and removing any trailing slashes
   */
  function formatUrlForDisplay(url: string): string {
    if (!url) return "";
    // Remove protocol and optional www
    let displayUrl = url.replace(/^https?:\/\/(www\.)?/i, '');
    // Remove trailing slashes
    displayUrl = displayUrl.replace(/\/+$/, '');
    // Capitalize first letter
    return displayUrl.charAt(0).toUpperCase() + displayUrl.slice(1);
  }

  // Process the homepage URL
  $: homepage = $store?.homepage ? sanitizeUrl($store.homepage) : "";
  $: websiteDisplay = homepage ? formatUrlForDisplay(homepage) : "";

  // Default SVG paths from public folder
  const defaultWebsiteIconSrc = "/theme/demo2025/images/social/website.svg";
  const defaultEmailIconSrc = "/theme/demo2025/images/social/email.svg";
  const defaultPhoneIconSrc = "/theme/demo2025/images/social/phone.svg";

  // Calculate icon size based on Stylebook font size variables
  let iconSizeClass = '';
  $: {
    switch (iconSize) {
      case 'small':
        iconSizeClass = 'icon-size-sm';
        break;
      case 'large':
        iconSizeClass = 'icon-size-lg';
        break;
      default:
        iconSizeClass = 'icon-size-base';
    }
  }

  function sanitizePhone(phone: string | null | undefined): string {
    if (!phone) return "";
    
    // Keep only digits, spaces, plus signs, parentheses, and hyphens
    return phone.replace(/[^\d\s+()-]/g, '');
  }
  
  function sanitizeEmail(email: string | null | undefined): string {
    if (!email) return "";
    
    // Simple validation of email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? email : "";
  }
  
  $: phoneNumber = $store?.phone ? sanitizePhone($store.phone) : "";
  $: emailAddress = $store?.email ? sanitizeEmail($store.email) : "";
</script>

<div class="contact-info">
  {#if homepage}
    <div class="info-wrapper">
      <div class={`toggle-icon-wrapper ${useIcons ? 'useIcons' : ''} ${iconSizeClass}`}>
        {#if websiteIconSrc}
          <img src={websiteIconSrc} alt="Website icon" />
        {:else if useIcons}
          <img src={defaultWebsiteIconSrc} alt="Website icon" />
        {/if}
      </div>
      <a
        href={homepage}
        target="_blank"
        rel="noopener noreferrer"
        class="link">
        {websiteDisplay}
      </a>
    </div>
  {/if}

  {#if emailAddress}
    <div class="info-wrapper">
      <div class={`toggle-icon-wrapper ${useIcons ? 'useIcons' : ''} ${iconSizeClass}`}>
        {#if emailIconSrc}
          <img src={emailIconSrc} alt="Email icon" />
        {:else if useIcons}
          <img src={defaultEmailIconSrc} alt="Email icon" />
        {/if}
      </div>
      <a href={`mailto:${emailAddress}`} class="link">
        {emailAddress}
      </a>
    </div>
  {/if}

  {#if phoneNumber}
    <div class="info-wrapper">
      <div class={`toggle-icon-wrapper ${useIcons ? 'useIcons' : ''} ${iconSizeClass}`}>
        {#if phoneIconSrc}
          <img src={phoneIconSrc} alt="Phone icon" />
        {:else if useIcons}
          <img src={defaultPhoneIconSrc} alt="Phone icon" />
        {/if}
      </div>
      <a href={`tel:${phoneNumber}`} class="link">
        {phoneNumber}
      </a>
    </div>
  {/if}
</div>

<style lang="scss">
  /* Define icon size variables */
  :root {
    --icon-base-size: 24px;
    --icon-sm-scale: 0.75;  /* 75% of base size = 18px */
    --icon-lg-scale: 1.33;  /* 133% of base size = 32px */
  }

  .contact-info {
    margin: 0;
    display: grid;
    place-items: start;
    font-family: inherit;
    font-weight: inherit;
    font-size: inherit;
  }

  .info-wrapper {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    align-items: center;
  }

  .toggle-icon-wrapper {
    display: none;
    
    &.useIcons {
      display: block !important;
    }
    
    img {
      object-fit: contain;
    }

    &.icon-size-sm img {
      height: calc(var(--icon-base-size) * var(--icon-sm-scale));
      width: calc(var(--icon-base-size) * var(--icon-sm-scale));
    }

    &.icon-size-base img {
      height: var(--icon-base-size);
      width: var(--icon-base-size);
    }

    &.icon-size-lg img {
      height: calc(var(--icon-base-size) * var(--icon-lg-scale));
      width: calc(var(--icon-base-size) * var(--icon-lg-scale));
    }
  }

  .link {
    color: inherit;
    text-decoration: none;
    transition: opacity 0.2s;
    
    &:hover {
      opacity: 0.8;
    }
  }
</style> 